import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { PublicFiche } from '../../types';

// ── Helpers signaux principe ─────────────────────────────────────────────────
/** Libellé traduit d'un signal — `t` fourni par useTranslation() de l'appelant. */
export const signalLabel = (s: string, t: TFunction): string => {
  if (s.startsWith('publication:')) {
    const codes = s.replace('publication:', '').toUpperCase();
    const keys: Record<string, string> = {
      B:   'fiche.card.signal.pub_B',
      R:   'fiche.header.signal.pub_R',
      BR:  'fiche.header.signal.pub_BR',
      BI:  'fiche.header.signal.pub_BI',
      BRI: 'fiche.header.signal.pub_BRI',
    };
    return keys[codes] ? t(keys[codes]) : t('fiche.header.signal.publication', { codes });
  }
  if (s === 'regex:revirement')            return t('fiche.card.signal.revirement');
  if (s === 'regex:attendu_principe')      return t('fiche.header.signal.attendu_principe');
  if (s === 'regex:chapeau')               return t('fiche.header.signal.chapeau');
  if (s === 'regex:visa_principe')         return t('fiche.header.signal.visa_principe');
  if (s === 'regex:formation_haute_texte') return t('fiche.card.signal.formation_haute');
  if (s.startsWith('regex:attendu_x'))     return t('fiche.card.signal.attendu_repeated');
  if (s.startsWith('formation:'))          return t('fiche.card.signal.formation');
  if (s.startsWith('citation_count:'))     return t('fiche.card.signal.citing', { value: s.replace('citation_count:', '') });
  if (s.startsWith('arrets_impactes:'))    return t('fiche.card.signal.impacted');
  return s;
};

export const hasRevirement = (signals?: string[] | null): boolean =>
  !!(signals?.some(s => s === 'regex:revirement' || s.startsWith('formation:')));

export const hasAssemblee = (signals?: string[] | null): boolean =>
  !!(signals?.some(s => s === 'regex:formation_haute_texte' || s.startsWith('formation:')));

export const llmRaison = (signals?: string[] | null): string | null => {
  const s = signals?.find(s => s.startsWith('llm:✅'));
  return s ? s.replace(/^llm:✅\s*/, '') : null;
};

export const SignalBadge = ({ signal }: { signal: string }) => {
  const { t } = useTranslation();
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
      {signalLabel(signal, t)}
    </span>
  );
};

// ── PrincipeSection ───────────────────────────────────────────────────────────
interface PrincipeSectionProps {
  fiche: PublicFiche;
}

/**
 * Contenu interne d'une carte de principe.
 * À utiliser dans FicheCardPrincipe (wrapper avec border amber)
 * ou comme section additionnelle dans FicheCardBase.
 */
export const PrincipeSection = ({ fiche }: PrincipeSectionProps) => {
  const { t } = useTranslation();
  const signals        = fiche.principe_signals ?? [];
  const visibleSignals = signals.filter(s => !s.startsWith('llm:'));
  const raison         = llmRaison(signals);
  const isRevirement   = hasRevirement(signals);
  const isAssemblee    = hasAssemblee(signals);
  const score          = fiche.principe_score ?? null;
  const hasLLM         = signals.some(s => s.startsWith('llm:✅'));

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          {t('fiche.card.principle')}
        </span>
        {isAssemblee && (
          <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border border-violet-300">
            {t('fiche.card.plenary')}
          </span>
        )}
        {isRevirement && !isAssemblee && (
          <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-300">
            {t('fiche.card.revirement_short')}
          </span>
        )}
        {fiche.domaine && (
          <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            {fiche.domaine}
          </span>
        )}
        {score !== null && (
          <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground">
            {t('fiche.card.index', { score })}
          </span>
        )}
        <span className="text-[11px] text-muted-foreground ml-auto">
          {fiche.decision_date?.slice(0, 10)}
        </span>
      </div>

      <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
        {fiche.jurisdiction || fiche.chamber}
        {fiche.chamber && fiche.jurisdiction !== fiche.chamber ? ` — ${fiche.chamber}` : ''}
        {t('fiche.card.number_sep')}{fiche.number}
      </p>

      {fiche.probleme && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
          {fiche.probleme}
        </p>
      )}

      {raison && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400 italic mb-2">{raison}</p>
      )}

      {visibleSignals.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visibleSignals.map((s, i) => <SignalBadge key={i} signal={s} />)}
          {hasLLM && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border border-emerald-200">
              {t('fiche.card.ai_check')}
            </span>
          )}
        </div>
      )}
    </>
  );
};